import { t, useLanguage } from "@/lib/i18n";
import ReligionSelect from "./ReligionSelect";
import AddressFields from "./AddressFields";
import CardPhotoEditor from "./CardPhotoEditor";
import { memberPhoto, cardPhotoCrop } from "@/lib/memberPhoto";
import CasteGotraFields from "./CasteGotraFields";
import { spouseIds } from "@/lib/spouses";
import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail, fileUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Upload, X, ImageIcon, Video, Loader2 } from "lucide-react";
import { lineageOptions } from "@/lib/lineageOptions";
import { displayMemberName } from "@/lib/memberName";
import MemberDetailDrawer from "./MemberDetailDrawer";
import { useAuth } from "@/context/AuthContext";
import isEqual from "lodash/isEqual";
import MemberMediaUpload from "./MemberMediaUpload";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";

const NONE = "__none__";

const empty = {
  email: "", phone_number: "", profession: "", country: "India", state: "", city: "", address: "", religion: "",
  first_name: "", last_name: "", full_name: "", gotra: "", caste: "", gender: "", birth_date: "", birth_place: "",
  is_alive: true, date_of_passing: "", father_id: null, mother_id: null,
  spouse_id: null, spouse_ids: [], children_ids: [], photos: [], videos: [], audios: [], card_photo_id: null,
  social_links: { linkedin: "", facebook: "", website: "" }, notes: "",
};

const comparableDetails = (values) => ({
  ...Object.fromEntries(Object.entries(empty).map(([key, fallback]) => [key, values[key] ?? fallback])),
  email: (values.email || "").trim(),
  phone_number: (values.phone_number || "").trim(),
  children_ids: [...(values.children_ids || [])].sort(),
  social_links: Object.fromEntries(Object.keys(empty.social_links).map((key) => [key, values.social_links?.[key] || ""])),
});
const comparableMetadata = (values) => Object.fromEntries(Object.entries(comparableDetails(values))
  .filter(([key]) => !["photos", "videos", "audios", "card_photo_id"].includes(key)));

function RelativeSearch({ label, field, options, persons, selectedIds, query, onSearch, onSelect, onRemove, disabled = false }) {
  const searchId = `search-${label.toLowerCase()}`;
  const normalized = query.trim().toLocaleLowerCase();
  const matches = normalized ? options.filter((person) => !selectedIds.includes(person.id)
    && [displayMemberName(person), person.caste, person.gotra, person.birth_date].filter(Boolean).join(" ").toLocaleLowerCase().includes(normalized)) : [];
  return <div className="space-y-2">
    <Label htmlFor={searchId}>{t(label)}{field === "spouse_ids" && ` (${selectedIds.length}/4)`}</Label>
    {selectedIds.length === 0 && <p className="text-sm text-stone-500">{t("None")}</p>}
    {selectedIds.map((id) => <div key={id} className="flex items-center justify-between gap-2 rounded-lg border p-2">
      <button type="button" className="text-sm text-left text-amber-800" onClick={() => { const person = persons.find((item) => item.id === id); if (person) onSelect(person); }}>
        {displayMemberName(persons.find((person) => person.id === id)) || t("Unavailable member")}
      </button>
      <Button type="button" size="sm" variant="outline" aria-label={t("Remove {0}", { 0: t(label).toLowerCase() })} onClick={() => onRemove(id)}>{t("Remove")}</Button>
    </div>)}
    <Input id={searchId} data-testid={searchId} value={query} disabled={disabled} onChange={(event) => onSearch(event.target.value)}
      placeholder={t(disabled ? "Maximum four spouses" : "Search by name, caste, gotra or birth date")} />
    {normalized && !disabled && <div className="rounded-xl border border-[#E6DEC3] max-h-48 overflow-y-auto p-2 bg-white" data-testid={`${searchId}-results`}>
      {matches.length === 0 && <p role="status" className="text-sm text-stone-400 p-2">{t("No matching eligible members.")}</p>}
      {matches.map((person) => <button key={person.id} type="button" data-testid={`select-${label.toLowerCase()}-${person.id}`}
        className="block w-full text-sm text-left px-2 py-2 rounded-lg hover:bg-[#F4EFEB] text-amber-800" onClick={() => onSelect(person)}>
        {displayMemberName(person)}<span className="block text-xs text-stone-500">{[person.caste, person.gotra, person.birth_date].filter(Boolean).join(" · ")}</span>
      </button>)}
    </div>}
  </div>;
}

export default function MemberModal({ open, onClose, persons, editing, onSaved, gotras = [], castes = [], defaultGotra = "", defaultCaste = "", defaultReligion = "Hindu", initialValues = null, requestId = null, selfProfile = false, draftMode = false, onDraftSave }) {
  useLanguage();
  const { user } = useAuth();
  const savesDirectly = user?.role === "admin";
  const [form, setForm] = useState(empty);
  const [initialForm, setInitialForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [catalogValid, setCatalogValid] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [mediaLimits, setMediaLimits] = useState(null);
  const [mediaSelection, setMediaSelection] = useState(null), [pendingRemoval, setPendingRemoval] = useState(null), [mediaError, setMediaError] = useState("");
  const applyMedia = (data) => {
    const snapshot = { photos: data.photos || [], videos: data.videos || [], audios: data.audios || [], card_photo_id: data.card_photo_id || null, media_revision: data.media_revision || 0 };
    setForm((f) => ({ ...f, ...snapshot })); setInitialForm((f) => ({ ...f, ...snapshot }));
  };
  const refreshMedia = async () => {
    if (editing?.id) { const { data } = await api.get(`/persons/${editing.id}/media`); applyMedia(data); }
  };
  useEffect(() => {
    if (!open || !editing?.id || draftMode) return;
    let active = true;
    const refresh = async () => {
      try { const { data } = await api.get(`/persons/${editing.id}/media`); if (active) applyMedia(data); }
      catch (error) { if (active) setMediaError(formatApiErrorDetail(error.response?.data?.detail)); }
    };
    const changed = (event) => { if (event.detail === editing.id) refresh(); };
    refresh(); window.addEventListener("member-media-changed", changed);
    return () => { active = false; window.removeEventListener("member-media-changed", changed); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id, draftMode]);
  const maxPhotos = mediaLimits?.max_photos ?? 8;
  const maxVideos = mediaLimits?.max_videos ?? 3;
  useEffect(() => {
    if (open && !draftMode) {
      setMediaLimits(null);
      api.get("/settings/media").then(({ data }) => setMediaLimits(data))
        .catch(() => toast.error(t("Could not load upload limits. Reopen the form to retry.")));
    }
  }, [open, draftMode]);
  const [lineagePreview, setLineagePreview] = useState(null);
  const [childSearch, setChildSearch] = useState("");
  const [relativeSearch, setRelativeSearch] = useState({ father_id: "", mother_id: "", spouse_ids: "" });

  useEffect(() => {
    setLineagePreview(null);
    setChildSearch("");
    setRelativeSearch({ father_id: "", mother_id: "", spouse_ids: "" });
    setActiveTab("basic");
    const startingForm = editing
      ? { ...empty, ...editing, social_links: { ...empty.social_links, ...(editing.social_links || {}) } }
      : { ...empty, religion: defaultReligion, gender: "Male", caste: defaultCaste, gotra: defaultGotra, ...initialValues };
    if (!startingForm.first_name && startingForm.full_name) {
      const [first, ...rest] = startingForm.full_name.trim().split(/\s+/);
      startingForm.first_name = first;
      startingForm.last_name = rest.join(" ");
    }
    startingForm.spouse_ids = spouseIds(editing || initialValues || {});
    setForm(startingForm);
    setInitialForm(startingForm);
  }, [editing, open, defaultGotra, defaultCaste, defaultReligion, initialValues]);

  const unchanged = !!(editing || requestId) && isEqual(comparableDetails(form), comparableDetails(initialForm));

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, social_links: { ...f.social_links, [k]: v } }));

  const eligible = lineageOptions(persons, form, editing?.id);
  const invalidFather = form.father_id && !eligible.fathers.some((p) => p.id === form.father_id);
  const invalidMother = form.mother_id && !eligible.mothers.some((p) => p.id === form.mother_id);
  const invalidSpouse = form.spouse_ids.length > 4 || form.spouse_ids.some((id) => !eligible.spouses.some((p) => p.id === id));
  const invalidChildren = (form.children_ids || []).filter((id) => !eligible.children.some((p) => p.id === id));
  const listedChildIds = [...new Set([...initialForm.children_ids, ...form.children_ids])];
  const childQuery = childSearch.trim().toLocaleLowerCase();
  const matchingChildren = childQuery ? eligible.children.filter((p) => !form.children_ids.includes(p.id)
    && [displayMemberName(p), p.gotra, p.caste, p.birth_date].filter(Boolean).join(" ").toLocaleLowerCase().includes(childQuery)) : [];

  const upload = async (files, kind) => {
    if (uploading) return;
    if (editing?.id) { setMediaSelection(Array.from(files)); return; }
    if (!mediaLimits) { toast.error(t("Upload limits are not loaded yet. Please retry.")); return; }
    const maxMb = kind === "video" ? mediaLimits.max_video_mb : mediaLimits.max_photo_mb;
    if (Array.from(files).some((file) => file.size > maxMb * 1024 * 1024)) {
      toast.error(t("Each {kind} must be {size} MB or smaller", { kind: t(kind), size: maxMb }));
      return;
    }
    const list = kind === "video" ? form.videos : form.photos;
    const limit = kind === "video" ? maxVideos : maxPhotos;
    const arr = Array.from(files);
    if (list.length + arr.length > limit) {
      toast.error(t("At most {limit} {kind} files allowed", { limit, kind: t(kind) }));
      return;
    }
    setUploading(true);
    try {
      for (const file of arr) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, [kind === "video" ? "videos" : "photos"]: [...f[kind === "video" ? "videos" : "photos"], data] }));
      }
      toast.success(t("Uploaded"));
    } catch (e) {
      toast.error(t(formatApiErrorDetail(e.response?.data?.detail) || t("Upload failed")));
    } finally {
      setUploading(false);
    }
  };

  const removeMedia = (kind, id) => { setMediaError(""); setPendingRemoval({ kind, id, revision: form.media_revision || 0 }); };
  const confirmRemoval = async () => {
    const { kind, id } = pendingRemoval;
    const key = kind === "video" ? "videos" : "photos";
    if (editing?.id) {
      setUploading(true);
      try {
        const { data } = await api.delete(`/persons/${editing.id}/media/${id}`, { data: { revision: pendingRemoval.revision, confirm_card: true } });
        applyMedia(data); setPendingRemoval(null);
        window.dispatchEvent(new CustomEvent("member-media-changed", { detail: editing.id }));
      } catch (error) { setMediaError(formatApiErrorDetail(error.response?.data?.detail)); await refreshMedia(); }
      finally { setUploading(false); }
      return;
    }
    setForm((f) => ({ ...f, [key]: f[key].filter((m) => m.id !== id),
      card_photo_id: kind === "photo" && memberPhoto(f)?.id === id ? f.photos.find((p) => p.id !== id)?.id || null : f.card_photo_id }));
    setPendingRemoval(null);
  };
  const chooseCard = async (id) => {
    if (!editing?.id) { set("card_photo_id", id); return; }
    setUploading(true);
    try { const { data } = await api.put(`/persons/${editing.id}/media/card`, { photo_id: id, revision: form.media_revision || 0 }); applyMedia(data);
      window.dispatchEvent(new CustomEvent("member-media-changed", { detail: editing.id }));
    } catch (error) { setMediaError(formatApiErrorDetail(error.response?.data?.detail)); await refreshMedia(); }
    finally { setUploading(false); }
  };

  const toggleChild = (id) => {
    setForm((f) => ({
      ...f,
      children_ids: f.children_ids.includes(id) ? f.children_ids.filter((c) => c !== id) : [...f.children_ids, id],
    }));
  };

  const changeTab = async (nextTab) => {
    if (saving || uploading) return;
    const photo = memberPhoto(form);
    const original = initialForm.photos.find((item) => item.id === photo?.id);
    // New members keep the crop in their draft until the member is submitted.
    if (!draftMode && activeTab === "media" && editing?.id && photo &&
        !isEqual(cardPhotoCrop(photo), cardPhotoCrop(original))) {
      setUploading(true);
      try {
        const { data: crop } = await api.post("/card-photos", {
          path: photo.path, crop: cardPhotoCrop(photo), member_id: editing.id,
        });
        const { data } = await api.put(`/persons/${editing.id}/media/card`, {
          revision: form.media_revision || 0, photo_id: photo.id,
          card_path: crop.path, card_crop: cardPhotoCrop(photo),
        });
        applyMedia(data);
        window.dispatchEvent(new CustomEvent("member-media-changed", { detail: editing.id }));
      } catch (error) {
        setMediaError(formatApiErrorDetail(error.response?.data?.detail));
        return;
      } finally { setUploading(false); }
    }
    setActiveTab(nextTab);
  };

  const submit = async () => {
    if (unchanged || saving || uploading || !catalogValid) return;
    if (!form.first_name.trim()) {
      toast.error(t("First name is required"));
      return;
    }
    if (!draftMode && form.caste && !castes.some((c) => c.name === form.caste && c.religion === form.religion)) {
      toast.error(t("Select a Caste belonging to the selected Religion."));
      return;
    }
    if (!draftMode && form.gotra && !gotras.some((g) => g.name === form.gotra && g.caste === form.caste && g.religion === form.religion)) {
      toast.error(t("Select a Gotra belonging to the selected Caste."));
      return;
    }
    if (invalidFather || invalidMother || invalidSpouse || invalidChildren.length) {
      toast.error(t("Review the conflicting selections in the Lineage tab before saving."));
      return;
    }
    setSaving(true);
    const memberDetails = { ...form };
    delete memberDetails.id_proof_type;
    delete memberDetails.id_proof_number;
    delete memberDetails.verification_status;
    const payload = {
      ...memberDetails,
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      full_name: [form.first_name.trim(), form.last_name.trim()].filter(Boolean).join(" "),
      father_id: form.father_id || null,
      mother_id: form.mother_id || null,
      spouse_id: form.spouse_ids[0] || null,
      spouse_ids: form.spouse_ids,
    };
    try {
      if (draftMode) {
        const { photos, videos, audios, card_photo_id, ...details } = payload;
        await onDraftSave(details); onClose(); return;
      }
      const photo = memberPhoto(payload);
      const original = initialForm.photos.find((item) => item.id === photo?.id);
      if (photo && (!photo.card_path || !isEqual(cardPhotoCrop(photo), cardPhotoCrop(original)))) {
        const { data } = await api.post("/card-photos", { path: photo.path, crop: cardPhotoCrop(photo), ...(editing?.id ? { member_id: editing.id } : {}) });
        payload.photos = payload.photos.map((item) => item.id === photo.id ? { ...item, card_path: data.path } : item);
        setForm((current) => ({ ...current, photos: payload.photos }));
        if (editing?.id) {
          const { data: media } = await api.put(`/persons/${editing.id}/media/card`, { revision: form.media_revision || 0,
            photo_id: photo.id, card_path: data.path, card_crop: cardPhotoCrop(photo) });
          applyMedia(media); window.dispatchEvent(new CustomEvent("member-media-changed", { detail: editing.id }));
        }
      }
      if (editing?.id && !requestId && isEqual(comparableMetadata(form), comparableMetadata(initialForm))) {
        toast.success(t("Media saved immediately.")); onSaved(); onClose(); return;
      }
      if (requestId) await api.put(`/member-requests/${requestId}`, payload);
      else if (editing) await api.put(`/persons/${editing.id}`, payload);
      else await api.post(selfProfile ? "/self-profile" : "/persons", payload);
      toast.success(savesDirectly ? t("Member saved successfully.") : t("Request submitted for admin approval. Track it in My Requests."));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(t(formatApiErrorDetail(e.response?.data?.detail) || t("Save failed")));
    } finally {
      setSaving(false);
    }
  };


  return (
    <><Dialog open={open} onOpenChange={(o) => !o && !uploading && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid="member-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{editing ? t("Edit Family Member") : selfProfile ? t("Create My Profile") : t("Add Family Member")}</DialogTitle>
          <DialogDescription>{draftMode ? t("Changes are kept in this tree draft until the whole request is published.") : savesDirectly ? t("Changes are saved immediately.") : t("Changes become visible after admin approval. Track status and comments in My Requests.")}</DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={changeTab} className="mt-2">
          <TabsList className="grid grid-cols-2 sm:grid-cols-4 h-auto w-full">
            <TabsTrigger value="basic" data-testid="tab-basic">{t("Basic")}</TabsTrigger>
            <TabsTrigger value="lineage" data-testid="tab-lineage">{t("Lineage")}</TabsTrigger>
            {!draftMode && <TabsTrigger value="media" data-testid="tab-media">{t("Media")}</TabsTrigger>}
            <TabsTrigger value="social" data-testid="tab-social">{t("Social links")}</TabsTrigger>
          </TabsList>

          {/* BASIC */}
          <TabsContent value="basic" className="space-y-4 pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="input-first-name">{t("First Name *")}</Label>
                <Input id="input-first-name" required autoComplete="given-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="input-first-name" placeholder={t("e.g. Ramesh")} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="input-last-name">{t("Last Name")}</Label>
                <Input id="input-last-name" autoComplete="family-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="input-last-name" placeholder={t("e.g. Sharma")} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="input-member-email">{t("Email")}</Label>
                <Input id="input-member-email" type="email" autoComplete="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="input-member-email" placeholder="name@example.com" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="input-member-phone">{t("Phone number")}</Label>
                <Input id="input-member-phone" type="tel" autoComplete="tel" maxLength={40} value={form.phone_number || ""} onChange={(e) => set("phone_number", e.target.value)} data-testid="input-member-phone" placeholder={t("e.g. +91 98765 43210")} />
              </div>
            </div>
            <ReligionSelect id="input-member-religion" value={form.religion}
              onChange={(religion) => setForm((current) => ({ ...current, religion, caste: "", gotra: "" }))} />
            <div className="space-y-1.5">
              <Label htmlFor="input-member-profession">{t("Profession/Business")}</Label>
              <Input id="input-member-profession" maxLength={200} value={form.profession || ""} onChange={(e) => set("profession", e.target.value)} data-testid="input-member-profession" placeholder={t("e.g. Teacher, Farming, or Business name")} />
            </div>
            <AddressFields value={form} onChange={(values) => setForm((current) => ({ ...current, ...values }))} />
            <div className="space-y-1.5">
              <Label htmlFor="input-member-address">{t("Address")}</Label>
              <Textarea id="input-member-address" autoComplete="street-address" value={form.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="input-member-address" placeholder={t("House/building, street, locality, postal code")} rows={3} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <CasteGotraFields allowNew={draftMode} religion={form.religion} caste={form.caste} gotra={form.gotra} castes={castes} gotras={gotras}
                onValidityChange={setCatalogValid}
                onChange={(values) => setForm((current) => ({ ...current, ...values }))} />
              <fieldset className="col-span-2 min-w-0 space-y-2" data-testid="select-gender">
                <legend className="text-sm font-medium">{t("Gender")}</legend>
                <div className="flex flex-wrap gap-2">
                  {["Male", "Female", "Other"].map((gender) => (
                    <label key={gender || NONE} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer ${
                      (form.gender || "") === gender ? "border-amber-700 bg-amber-50" : "border-[#E6DEC3] bg-white"
                    }`}>
                      <input type="radio" name="member-gender" value={gender} checked={(form.gender || "") === gender}
                        onChange={() => set("gender", gender)} className="accent-amber-800"
                        data-testid={`gender-${gender.toLowerCase() || "unspecified"}`} />
                      {t(gender || "Unspecified")}
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <div className="min-w-0 space-y-1.5">
                <Label>{t("Birth Date")}</Label>
                <Input type="date" value={form.birth_date} onChange={(e) => set("birth_date", e.target.value)} data-testid="input-birth-date" />
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="input-birth-place">{t("Birth place")}</Label>
                <Input id="input-birth-place" value={form.birth_place || ""} onChange={(e) => set("birth_place", e.target.value)} data-testid="input-birth-place" placeholder={t("City, Country")} />
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="switch-is-alive" className="cursor-pointer">{t("Is this person living?")}</Label>
                <div className="h-10 flex items-center rounded-md border border-[#E6DEC3] px-3 bg-[#FAF7F2]">
                  <Switch id="switch-is-alive" checked={form.is_alive} onCheckedChange={(v) => set("is_alive", v)} data-testid="switch-is-alive" />
                </div>
              </div>
            </div>
            {!form.is_alive && (
              <div className="space-y-1.5">
                <Label>{t("Date of Passing")}</Label>
                <Input type="date" value={form.date_of_passing} onChange={(e) => set("date_of_passing", e.target.value)} data-testid="input-date-passing" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>{t("Notes")}</Label>
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="input-notes" placeholder={t("Life story, achievements…")} />
            </div>
          </TabsContent>

          {/* LINEAGE */}
          <TabsContent value="lineage" className="space-y-4 pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <RelativeSearch label="Father" field="father_id" options={eligible.fathers} persons={persons} selectedIds={form.father_id ? [form.father_id] : []}
                query={relativeSearch.father_id} onSearch={(value) => setRelativeSearch((current) => ({ ...current, father_id: value }))}
                onRemove={() => set("father_id", null)} onSelect={(person) => setLineagePreview({ person, field: "father_id", label: "father" })} />
              <RelativeSearch label="Mother" field="mother_id" options={eligible.mothers} persons={persons} selectedIds={form.mother_id ? [form.mother_id] : []}
                query={relativeSearch.mother_id} onSearch={(value) => setRelativeSearch((current) => ({ ...current, mother_id: value }))}
                onRemove={() => set("mother_id", null)} onSelect={(person) => setLineagePreview({ person, field: "mother_id", label: "mother" })} />
            </div>
            {invalidFather && <p role="alert" className="text-sm text-red-700">{t("Father must be a male member and cannot be this member, their spouse, or a descendant. Choose another father or None.")}</p>}
            {invalidMother && <p role="alert" className="text-sm text-red-700">{t("Mother must be a female member and cannot be this member, their spouse, or a descendant. Choose another mother or None.")}</p>}
            <RelativeSearch label="Spouse" field="spouse_ids" options={eligible.spouses} persons={persons} selectedIds={form.spouse_ids}
              query={relativeSearch.spouse_ids} disabled={form.spouse_ids.length >= 4}
              onSearch={(value) => setRelativeSearch((current) => ({ ...current, spouse_ids: value }))}
              onRemove={(id) => set("spouse_ids", form.spouse_ids.filter((sid) => sid !== id))}
              onSelect={(person) => setLineagePreview({ person, field: "spouse_ids", label: "spouse" })} />
            {!["Male", "Female"].includes(form.gender) && <p className="text-xs text-stone-500">{t("Select Male or Female on the Basic tab to list opposite-gender spouses.")}</p>}
            {invalidSpouse && <p role="alert" className="text-sm text-red-700">{t("The selected spouse must be of the opposite gender and cannot be an ancestor or descendant. Choose another spouse or None.")}</p>}
            <div className="space-y-2">
              <Label>{t("Children")}</Label>
              <div className="rounded-xl border border-[#E6DEC3] max-h-48 overflow-y-auto p-2 bg-white" data-testid="children-list">
                {listedChildIds.length === 0 && <p className="text-sm text-stone-400 p-2">{t("No children selected.")}</p>}
                {listedChildIds.map((id) => {
                  const child = persons.find((p) => p.id === id);
                  return <div key={id} className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-[#F4EFEB]">
                    <Checkbox id={`child-${id}`} aria-label={t("Select {0} as child", { 0: displayMemberName(child) || t("Unavailable member") })}
                      checked={form.children_ids.includes(id)} onCheckedChange={() => toggleChild(id)} data-testid={`child-checkbox-${id}`} />
                    <Label htmlFor={`child-${id}`} className="text-sm flex-1 cursor-pointer">
                      {displayMemberName(child) || t("Unavailable member")}
                      <span className="block text-xs text-stone-500">{[child?.gotra, child?.birth_date].filter(Boolean).join(" · ")}</span>
                    </Label>
                  </div>;
                })}
              </div>
              <p className="text-xs text-stone-500">{t("Uncheck a child to remove the relationship when you save.")}</p>
              <Label htmlFor="search-children">{t("Search children to add")}</Label>
              <Input id="search-children" data-testid="search-children" value={childSearch}
                onChange={(e) => setChildSearch(e.target.value)} placeholder={t("Search by name, caste, gotra or birth date")} />
              {childQuery && <div className="rounded-xl border border-[#E6DEC3] max-h-48 overflow-y-auto p-2 bg-white" data-testid="children-search-results">
                {matchingChildren.length === 0 && <p role="status" className="text-sm text-stone-400 p-2">{t("No matching eligible children.")}</p>}
                {matchingChildren.map((p) => <button key={p.id} type="button" data-testid={`add-child-${p.id}`}
                  className="block w-full text-sm text-left px-2 py-2 rounded-lg hover:bg-[#F4EFEB] text-amber-800"
                  onClick={() => setLineagePreview({ person: p, field: "children_ids", label: "child" })}>
                  {displayMemberName(p)}<span className="block text-xs text-stone-500">{[p.caste, p.gotra, p.birth_date].filter(Boolean).join(" · ")}</span>
                </button>)}
              </div>}
              {invalidChildren.length > 0 && <div role="alert" className="text-sm text-red-700 space-y-2">
                <p>{t("Remove conflicting children selections (ancestors, spouse, or unavailable members):")}</p>
                {invalidChildren.map((id) => <Button key={id} type="button" variant="outline" onClick={() => toggleChild(id)}>{t("Remove ")}{displayMemberName(persons.find((p) => p.id === id)) || t("unavailable member")}</Button>)}
              </div>}
            </div>
          </TabsContent>

          {/* MEDIA */}
          {!draftMode && <TabsContent value="media" className="space-y-6 pt-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="flex items-center gap-2"><ImageIcon size={16} />{t(" Photos (")}{form.photos.length}/{maxPhotos})</Label>
                {mediaLimits && maxPhotos > 0 && <label className="cursor-pointer">
                  <input type="file" accept="image/*" multiple hidden disabled={uploading} onChange={(e) => { upload(e.target.files, "photo"); e.target.value = ""; }} data-testid="upload-photo-input" />
                  <span className="inline-flex items-center gap-1 text-sm text-amber-800 font-medium hover:underline">
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}{t(" Add")}</span>
                </label>}
              </div>
              <p className="text-xs text-stone-600 mb-2">{mediaLimits ? t("Maximum {0} MB per photo. ", { 0: mediaLimits.max_photo_mb }) : t("Loading upload limits... ")}{t("Select a photo for the member card.")}</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {form.photos.map((m) => (
                  <div key={m.id} className="relative group aspect-square rounded-lg overflow-hidden border border-[#E6DEC3]">
                    <img src={fileUrl(m.path)} alt={m.filename} className="w-full h-full object-cover" />
                    <button type="button" disabled={uploading} aria-pressed={(form.card_photo_id || form.photos[0]?.id) === m.id} onClick={() => chooseCard(m.id)} className="absolute bottom-0 inset-x-0 bg-white/90 text-xs py-1 text-amber-900">
                      {(form.card_photo_id || form.photos[0]?.id) === m.id ? t("Card photo") : t("Use on card")}
                    </button>
                    <button aria-label={t("Remove photo {0}", { 0: m.filename })} onClick={() => removeMedia("photo", m.id)} className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition-opacity" data-testid={`remove-photo-${m.id}`}><X size={12} /></button>
                  </div>
                ))}
              </div>
              <fieldset disabled={saving || uploading}>
              <CardPhotoEditor photo={memberPhoto(form)} name={displayMemberName(form)} onChange={(card_crop) => {
                setForm((current) => ({ ...current, photos: current.photos.map((photo) =>
                  photo.id === memberPhoto(current)?.id ? { ...photo, card_crop } : photo) }));
              }} />
              </fieldset>

            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="flex items-center gap-2"><Video size={16} />{t(" Videos (")}{form.videos.length}/{maxVideos})</Label>
                {mediaLimits && maxVideos > 0 && <label className="cursor-pointer">
                  <input type="file" accept="video/*" multiple hidden disabled={uploading} onChange={(e) => { upload(e.target.files, "video"); e.target.value = ""; }} data-testid="upload-video-input" />
                  <span className="inline-flex items-center gap-1 text-sm text-amber-800 font-medium hover:underline">
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}{t(" Add")}</span>
                </label>}
              </div>
              <div className="space-y-2">
                <p className="text-xs text-stone-600">{mediaLimits ? t("Maximum {0} MB per video.", { 0: mediaLimits.max_video_mb }) : t("Loading upload limits...")}</p>
                {form.videos.map((m) => (
                  <div key={m.id} className="flex items-center justify-between rounded-lg border border-[#E6DEC3] px-3 py-2 bg-white">
                    <span className="text-sm flex items-center gap-2 truncate"><Video size={14} className="text-amber-800" /> {m.filename}</span>
                    <button aria-label={t("Remove video {0}", { 0: m.filename })} onClick={() => removeMedia("video", m.id)} className="text-red-600" data-testid={`remove-video-${m.id}`}><X size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>}

          {/* SOCIAL LINKS */}
          <TabsContent value="social" className="space-y-4 pt-4">
            <div className="space-y-1.5">
              <Label>{t("LinkedIn")}</Label>
              <Input value={form.social_links.linkedin} onChange={(e) => setSocial("linkedin", e.target.value)} data-testid="input-linkedin" placeholder="https://linkedin.com/in/…" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t("Facebook")}</Label>
                <Input value={form.social_links.facebook} onChange={(e) => setSocial("facebook", e.target.value)} data-testid="input-facebook" placeholder={t("facebook.com/…")} />
              </div>
              <div className="space-y-1.5">
                <Label>{t("Website")}</Label>
                <Input value={form.social_links.website} onChange={(e) => setSocial("website", e.target.value)} data-testid="input-website" placeholder="https://…" />
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t border-[#E6DEC3] mt-2">
          <Button variant="outline" onClick={onClose} data-testid="member-cancel-button">{t("Cancel")}</Button>
          <Button onClick={submit} disabled={saving || uploading || unchanged || !catalogValid} className="gold-btn text-[#2C1E16] font-semibold border-0" data-testid="member-save-button">
            {saving ? t("Submitting…") : draftMode ? t("Save in draft") : requestId ? t("Resubmit for Approval") : t("Submit for Approval")}
          </Button>
        </div>
      </DialogContent>
      {open && lineagePreview && <MemberDetailDrawer person={lineagePreview.person} persons={persons} previewOnly
        onClose={() => setLineagePreview(null)}
        selectionActions={<div className="space-y-2 mb-4">
          <p className="text-sm">{t("Review this member's lineage before selecting them as ")}{t(lineagePreview.label)}.</p>
          <p className="text-xs text-stone-500">{t("The relationship will be saved when you save the member form.")}</p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" data-testid="lineage-preview-confirm" onClick={() => {
              const { person: relative, field } = lineagePreview;
              if (field === "children_ids") setForm((current) => ({ ...current, children_ids: [...new Set([...current.children_ids, relative.id])] }));
              else if (field === "spouse_ids") setForm((current) => ({ ...current, spouse_ids: [...new Set([...current.spouse_ids, relative.id])].slice(0, 4) }));
              else if (field === "father_id" && !editing && !requestId) {
                const spouses = spouseIds(relative);
                setForm((current) => ({ ...current, father_id: relative.id,
                  mother_id: current.mother_id || (spouses.length === 1 ? spouses[0] : null) }));
              }
              else set(field, relative.id);
              setRelativeSearch((current) => ({ ...current, [field]: "" }));
              setLineagePreview(null);
            }}>{t("Select as ")}{t(lineagePreview.label)}</Button>
            <Button type="button" variant="outline" onClick={() => setLineagePreview(null)}>{t("Back to form")}</Button>
          </div>
        </div>} />}
    </Dialog>
    <Dialog open={!!mediaSelection} onOpenChange={(value) => { if (!value && !uploading) setMediaSelection(null); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85dvh] overflow-auto"><DialogHeader><DialogTitle>{t("Upload photos / videos")}</DialogTitle><DialogDescription>{t("Upload photos and videos for this member.")}</DialogDescription></DialogHeader>
        {mediaSelection && <MemberMediaUpload memberId={editing.id} initialFiles={mediaSelection} onBusy={setUploading} onComplete={() => setMediaSelection(null)} onChanged={async () => {
          await refreshMedia(); window.dispatchEvent(new CustomEvent("member-media-changed", { detail: editing.id }));
        }} />}
      </DialogContent>
    </Dialog>
    <AlertDialog open={!!pendingRemoval || !!mediaError} onOpenChange={(value) => { if (!value && !uploading) { setPendingRemoval(null); setMediaError(""); } }}>
      <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>{t(pendingRemoval ? "Remove media" : "Media update failed")}</AlertDialogTitle>
        <AlertDialogDescription>{t(pendingRemoval?.kind === "photo" && memberPhoto(form)?.id === pendingRemoval?.id
          ? "This photo is used on the tree card. Removing it will use the first remaining default photo, or the placeholder when none remain." : "Review this media change before continuing.")}</AlertDialogDescription></AlertDialogHeader>
        {mediaError && <p role="alert" className="text-red-800">{t(mediaError)}</p>}
        <AlertDialogFooter><AlertDialogCancel disabled={uploading}>{t("Cancel")}</AlertDialogCancel>{pendingRemoval && <Button variant="destructive" disabled={uploading || !!mediaError} onClick={confirmRemoval}>{t("Confirm deletion")}</Button>}</AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog></>
  );
}
